import { mean, median } from './math';
import { unwrappedCents } from './note';

import type { PitchSample } from './types';

/**
 * series — the held note as a curve the result screen can draw: the breath chart
 * (#56, "3 points joined by lines" → the take itself) and the vibrato wave (#57, a
 * synthetic sine → the take itself).
 *
 * Cents around the take's own median, so the curve says how the pitch moved and never
 * which note it was (no absolute pitch on the result screen). Octave errors are
 * unwrapped with the same function the vibrato analysis uses (`unwrappedCents`); the
 * centre differs — one median for the whole take here, one per voiced run there.
 *
 * Time is taken from the samples as given — a silence stays as empty slots. A caller
 * whose timestamps include a pause folds it before this (the web capture does, so the
 * curve and the breath sections share one time axis).
 *
 * Framework-free and pure — vitest target.
 */

/** One value per this many ms of the take — 20 a second, enough to draw a 6 Hz vibrato. */
export const PITCH_SERIES_INTERVAL_MS = 50;

/**
 * Most values a series holds. At 50 ms that is 20 s — the whole analysis window the
 * capture keeps (`PCM_KEEP_SEC`), so a take the report is built from stays at about 50 ms
 * (one right at the window edge gets 51).
 * Only a longer one widens the interval, rather than being cut. The series rides in
 * every stored Record (up to 50 of them): 400 small integers is a few kB.
 */
export const PITCH_SERIES_MAX_POINTS = 400;

export interface PitchSeries {
  /** Milliseconds between values; the first value is the take's first reading. */
  intervalMs: number;
  /**
   * Whole cents around the take median. `null` where nothing was voiced — a breath
   * is a gap in the curve, not a line across it.
   */
  values: (number | null)[];
}

/** `null` when there is no voiced reading at all — not an empty curve. */
export const toPitchSeries = (samples: PitchSample[]): PitchSeries | null => {
  if (samples.length === 0) {
    return null;
  }
  const f0 = samples.map((s) => s.f0Hz);
  const cents = unwrappedCents(f0, median(f0));
  const origin = samples[0].tMs;
  const times = samples.map((s) => s.tMs - origin);
  const spanMs = times[times.length - 1];
  const intervalMs = Math.max(
    PITCH_SERIES_INTERVAL_MS,
    Math.ceil((spanMs + 1) / PITCH_SERIES_MAX_POINTS),
  );
  const buckets: number[][] = Array.from(
    { length: Math.floor(spanMs / intervalMs) + 1 },
    () => [],
  );
  times.forEach((t, i) => {
    buckets[Math.floor(t / intervalMs)].push(cents[i]);
  });
  return {
    intervalMs,
    values: buckets.map((b) => (b.length === 0 ? null : Math.round(mean(b)))),
  };
};
